import { ChevronDown, ChevronUp, ListTree, Minus, Plus, Search, X } from 'lucide-react';
import {
  createContext,
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { OutlineChevron, OutlineLevelBar, useOutlineFold, visibleOwnerOf } from '../outline-fold';
import { findAll, indexText, MAX_SEARCH_RESULTS, rangesOf } from './search-text';

/**
 * Thanh công cụ của bản xem trước và giao kèo trình xem ↔ thanh — APM-PVW-a.
 *
 * ## Hai kiểu zoom, vì hai loại tài liệu cần hai thứ khác nhau
 *
 * - `scale` (ảnh, PDF, pptx, docx): trang có **khổ cố định**. Phóng to là phóng
 *   **hình** — nội dung lớn hơn khung thì cuộn cả hai chiều, như Acrobat.
 * - `reflow` (markdown, chilldoc, text, code): chữ **không có khổ**. Phóng to là
 *   tăng **cỡ chữ** và dàn lại dòng theo bề ngang còn lại, như zoom của trình
 *   duyệt — không bao giờ có thanh cuộn ngang. Làm bằng CSS `zoom` chứ không
 *   `transform: scale`: `zoom` dàn lại theo bề ngang, `scale` chỉ phóng ảnh đã
 *   dàn rồi tràn ra ngoài.
 *
 * Kiểu zoom khai **tĩnh** ở `viewer-registry` (nó chỉ phụ thuộc loại tệp), nên
 * thanh hiện ngay cả lúc trình xem còn đang tải. Thứ chỉ biết **lúc chạy** (mức
 * "vừa khung" tính ra bao nhiêu %, và ở các task sau: trang, outline, tìm) thì
 * trình xem khai qua `useRegisterViewer`.
 */

export type ZoomKind = 'scale' | 'reflow';
/** `'fit'` = vừa khung (mặc định); số = phần trăm. */
export type ScaleZoom = 'fit' | number;

export const SCALE_LEVELS: readonly number[] = [
  25, 50, 67, 80, 90, 100, 110, 125, 150, 175, 200, 250, 300, 400,
];
export const REFLOW_LEVELS: readonly number[] = [80, 90, 100, 110, 125, 150, 175, 200];

/**
 * Mức kế tiếp trên dãy — lớn hơn **hẳn** `current` (hay nhỏ hơn hẳn), kẹp ở hai
 * đầu. `current` không cần nằm trên dãy: "vừa khung" ra 73 % thì `+` là 80 %.
 */
export function stepZoom(levels: readonly number[], current: number, direction: 1 | -1): number {
  if (direction === 1) return levels.find((level) => level > current + 0.5) ?? levels.at(-1)!;
  return [...levels].reverse().find((level) => level < current - 0.5) ?? levels[0]!;
}

const STORAGE_KEY = (kind: ZoomKind): string => `apm.preview.zoom.${kind}`;
const OUTLINE_KEY = 'apm.preview.outline';

const OUTLINE_WIDTH_KEY = 'apm.preview.outline.width';
export const OUTLINE_DEFAULT_WIDTH = 256;
export const OUTLINE_MIN_WIDTH = 180;
export const OUTLINE_MAX_WIDTH = 480;

export function clampOutlineWidth(width: number): number {
  if (!Number.isFinite(width)) return OUTLINE_DEFAULT_WIDTH;
  return Math.round(Math.min(OUTLINE_MAX_WIDTH, Math.max(OUTLINE_MIN_WIDTH, width)));
}

function readStoredOutlineWidth(): number {
  try {
    const raw = window.localStorage.getItem(OUTLINE_WIDTH_KEY);
    return raw === null ? OUTLINE_DEFAULT_WIDTH : clampOutlineWidth(Number(raw));
  } catch {
    return OUTLINE_DEFAULT_WIDTH;
  }
}

function storeOutlineWidth(width: number): void {
  try {
    window.localStorage.setItem(OUTLINE_WIDTH_KEY, String(width));
  } catch {
    // Không nhớ được thì lần sau về mặc định.
  }
}

function readStoredOutline(): boolean {
  try {
    return window.localStorage.getItem(OUTLINE_KEY) === '1';
  } catch {
    return false;
  }
}

function storeOutline(open: boolean): void {
  try {
    window.localStorage.setItem(OUTLINE_KEY, open ? '1' : '0');
  } catch {
    // Không nhớ được thì lần sau đóng — không phải lỗi.
  }
}

/** Đọc mức đã nhớ; `localStorage` ném (chế độ riêng tư, bị chặn) hay giá trị lạ ⇒ mặc định. */
export function readStoredZoom(kind: 'scale'): ScaleZoom;
export function readStoredZoom(kind: 'reflow'): number;
export function readStoredZoom(kind: ZoomKind): ScaleZoom {
  const fallback = kind === 'scale' ? 'fit' : 100;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY(kind));
    if (raw === null) return fallback;
    if (kind === 'scale' && raw === 'fit') return 'fit';
    const value = Number(raw);
    const levels = kind === 'scale' ? SCALE_LEVELS : REFLOW_LEVELS;
    return Number.isFinite(value) && value >= levels[0]! && value <= levels.at(-1)!
      ? value
      : fallback;
  } catch {
    return fallback;
  }
}

function storeZoom(kind: ZoomKind, value: ScaleZoom): void {
  try {
    window.localStorage.setItem(STORAGE_KEY(kind), String(value));
  } catch {
    // Không nhớ được thì lần sau về mặc định — không phải lỗi.
  }
}

/** Tài liệu chia trang — APM-PVW-b. `current`/`total` đếm từ 1. */
export interface ViewerPages {
  readonly current: number;
  readonly total: number;
  readonly goTo: (page: number) => void;
}

/** Một mục dàn ý — APM-PVW-c. `level` 1–6; `id` chỉ trình xem hiểu. */
export interface PreviewOutlineEntry {
  readonly id: string;
  readonly level: number;
  readonly text: string;
}

export interface ViewerOutline {
  /** Trình xem phải giữ **cùng mảng** giữa các lần render (memo) — đổi mảng là khai lại. */
  readonly entries: readonly PreviewOutlineEntry[];
  /** Mục đang đọc (theo vị trí cuộn) — tô đậm ở cột dàn ý. */
  readonly active?: string | undefined;
  readonly goTo: (id: string) => void;
  /**
   * Có ⇒ cột dàn ý vẽ mỗi mục thành một **bản chụp nhỏ** thay vì một dòng chữ
   * (pptx: slide không có tiêu đề đáng tin, mà "Slide 7" thì chẳng nói lên gì —
   * người dùng nhận ra slide bằng mắt). `text` của mục vẫn là nhãn trợ năng.
   */
  readonly thumbnails?: ViewerOutlineThumbnails | undefined;
}

/** Bản chụp nhỏ cho từng mục dàn ý — pptx (người dùng yêu cầu 2026-10-05). */
export interface ViewerOutlineThumbnails {
  /** Rộng / cao của một bản chụp — khung giữ chỗ đúng tỉ lệ trước khi vẽ, không nhảy bố cục. */
  readonly aspect: number;
  /**
   * Vẽ mục `id` vào `container` (rỗng) ở bề ngang `width` px; trả hàm dọn, hoặc
   * `null` nếu chưa vẽ được. Chỉ gọi cho mục đang trong (hoặc gần) khung nhìn —
   * cột tự quan sát giao cắt, trình xem không phải lo.
   */
  readonly render: (id: string, container: HTMLElement, width: number) => (() => void) | null;
}

/** Thứ trình xem khai lúc chạy. Task d thêm `search`. */
export interface ViewerCapabilities {
  /** Mức "vừa khung" tương ứng bao nhiêu % — để `+`/`−` đi tiếp từ đúng chỗ đang thấy. */
  readonly fitPercent?: number | undefined;
  readonly pages?: ViewerPages | undefined;
  readonly outline?: ViewerOutline | undefined;
  /** Trình xem tự tìm (PDF: chữ nằm trong canvas). Trình xem DOM thì để thanh công cụ tìm. */
  readonly search?: ViewerSearch | undefined;
}

/** Tìm theo từ khoá — APM-PVW-d. */
export interface ViewerSearch {
  /** Tìm và tô mọi kết quả; trả số kết quả (≤ `MAX_SEARCH_RESULTS`). `signal` huỷ khi từ khoá đổi. */
  readonly run: (query: string, signal: AbortSignal) => Promise<number>;
  /** Tô đậm kết quả thứ `index` (từ 0) và cuộn tới nó. */
  readonly go: (index: number) => void;
  readonly clear: () => void;
}

/** Số trang gõ tay ⇒ trang hợp lệ: kẹp vào 1…`total`, chữ rác ⇒ 1. */
export function clampPage(value: number, total: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(total, Math.max(1, Math.round(value)));
}

interface ControlsContextValue {
  readonly scale: ScaleZoom;
  readonly register: (caps: ViewerCapabilities) => void;
}

const ControlsContext = createContext<ControlsContextValue>({
  scale: 'fit',
  register: () => undefined,
});

/** Mức zoom kiểu `scale` mà trình xem phải vẽ theo. Ngoài khung xem trước thì luôn `'fit'`. */
export function usePreviewScale(): ScaleZoom {
  return useContext(ControlsContext).scale;
}

/**
 * Khai khả năng lúc chạy. Gọi lại mỗi khi giá trị đổi; gỡ trình xem thì khai rỗng.
 *
 * Hàm (`goTo`) đi qua một ref: trình xem tạo hàm mới mỗi lần render, và đưa
 * thẳng nó vào phụ thuộc là khai lại — tức render lại thanh công cụ — mỗi khung
 * hình khi đang cuộn.
 */
export function useRegisterViewer(caps: ViewerCapabilities): void {
  const { register } = useContext(ControlsContext);
  const latest = useRef(caps);
  latest.current = caps;
  const { fitPercent } = caps;
  const current = caps.pages?.current;
  const total = caps.pages?.total;
  const entries = caps.outline?.entries;
  const active = caps.outline?.active;
  const thumbnailAspect = caps.outline?.thumbnails?.aspect;
  const searchable = caps.search !== undefined;
  useEffect(() => {
    register({
      fitPercent,
      pages:
        total === undefined || total < 1
          ? undefined
          : {
              current: current ?? 1,
              total,
              goTo: (page) => latest.current.pages?.goTo(page),
            },
      outline:
        entries === undefined || entries.length === 0
          ? undefined
          : {
              entries,
              active,
              goTo: (id) => latest.current.outline?.goTo(id),
              thumbnails:
                thumbnailAspect === undefined
                  ? undefined
                  : {
                      aspect: thumbnailAspect,
                      render: (id, container, width) =>
                        latest.current.outline?.thumbnails?.render(id, container, width) ?? null,
                    },
            },
      search: searchable
        ? {
            run: (query, signal) => latest.current.search?.run(query, signal) ?? Promise.resolve(0),
            go: (index) => latest.current.search?.go(index),
            clear: () => latest.current.search?.clear(),
          }
        : undefined,
    });
  }, [register, fitPercent, current, total, entries, active, thumbnailAspect, searchable]);
  useEffect(() => () => register({}), [register]);
}

/**
 * Khung của một lượt xem: thanh công cụ + vùng cuộn + context cho trình xem.
 *
 * Vùng cuộn nằm **ở đây** chứ không ở khung ngoài, để thanh công cụ đứng yên
 * khi cuộn tài liệu. Ở trang chia sẻ (khung ngoài không có chiều cao cố định)
 * vùng này cao theo nội dung, trang cuộn, và thanh `sticky` vẫn bám đầu trang.
 */
export function PreviewControls({
  kind,
  domSearch = false,
  toolbarSlot = null,
  children,
}: {
  readonly kind: ZoomKind | undefined;
  /** Tìm ngay trên DOM của vùng xem (md, chilldoc, docx, text, code) — APM-PVW-d. */
  readonly domSearch?: boolean;
  /**
   * Chỗ đặt thanh công cụ **trong header của hộp** (2026-10-02): hộp xem trước
   * có header riêng (tên, tải về, đóng), và một thanh công cụ nằm dưới nó là
   * mất thêm ~40 px chiều cao ở mọi định dạng. Có ô thì thanh vẽ vào đó qua
   * portal, không thì vẽ như cũ ngay trên vùng xem.
   */
  readonly toolbarSlot?: HTMLElement | null;
  readonly children: ReactNode;
}): ReactNode {
  const rootRef = useRef<HTMLDivElement>(null);
  const [scale, setScaleState] = useState<ScaleZoom>(() => readStoredZoom('scale'));
  const [reflow, setReflowState] = useState<number>(() => readStoredZoom('reflow'));
  const [caps, setCaps] = useState<ViewerCapabilities>({});
  const [outlineOpen, setOutlineOpen] = useState(readStoredOutline);
  const toggleOutline = useCallback(() => {
    setOutlineOpen((open) => {
      storeOutline(!open);
      return !open;
    });
  }, []);
  const outline = caps.outline;
  const areaRef = useRef<HTMLDivElement>(null);
  const domSearcher = useMemo(
    () => (domSearch ? createDomSearch(() => areaRef.current) : undefined),
    [domSearch],
  );
  const search = caps.search ?? domSearcher;
  const [searchOpen, setSearchOpen] = useState(false);
  // Tăng mỗi lần Ctrl+F: ô tìm đang mở vẫn phải được focus lại và chọn hết chữ.
  const [searchFocus, setSearchFocus] = useState(0);
  const searchRef = useRef(search);
  searchRef.current = search;

  const setScale = useCallback((value: ScaleZoom) => {
    setScaleState(value);
    storeZoom('scale', value);
  }, []);
  const setReflow = useCallback((value: number) => {
    setReflowState(value);
    storeZoom('reflow', value);
  }, []);

  // Phần trăm đang thấy — "vừa khung" quy ra số nếu trình xem đã khai.
  const effectiveScale = scale === 'fit' ? (caps.fitPercent ?? 100) : scale;

  const zoomBy = useCallback(
    (direction: 1 | -1) => {
      if (kind === 'scale') setScale(stepZoom(SCALE_LEVELS, effectiveScale, direction));
      if (kind === 'reflow') setReflow(stepZoom(REFLOW_LEVELS, reflow, direction));
    },
    [kind, effectiveScale, reflow, setScale, setReflow],
  );
  const zoomReset = useCallback(() => {
    if (kind === 'scale') setScale('fit');
    if (kind === 'reflow') setReflow(100);
  }, [kind, setScale, setReflow]);

  // Ctrl/⌘ + − 0 và Ctrl/⌘ + lăn chuột: zoom tài liệu, không zoom cả trang.
  // PageUp/PageDown: trang trước/sau (APM-PVW-b).
  const zoomByRef = useRef(zoomBy);
  zoomByRef.current = zoomBy;
  const zoomResetRef = useRef(zoomReset);
  zoomResetRef.current = zoomReset;
  const pagesRef = useRef(caps.pages);
  pagesRef.current = caps.pages;
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // Trong hộp xem: focus nằm trong hộp thoại chứa nó (nút Đóng ở header cũng
    // tính). Ở trang chia sẻ (không có hộp thoại): cả trang.
    const scope = root.closest('[role="dialog"]') ?? document.body;
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as Node | null;
      if (target !== document.body && !(target && scope.contains(target))) return;
      const pages = pagesRef.current;
      if (
        pages &&
        (event.key === 'PageDown' || event.key === 'PageUp') &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !isEditable(target)
      ) {
        event.preventDefault();
        pages.goTo(clampPage(pages.current + (event.key === 'PageDown' ? 1 : -1), pages.total));
        return;
      }
      // Ctrl/⌘ F: ô tìm của bản xem, không phải của trình duyệt — ô ấy không thấy
      // chữ trong canvas PDF hay trang ảo hoá chưa vẽ.
      if (
        searchRef.current &&
        (event.ctrlKey || event.metaKey) &&
        !event.altKey &&
        event.key.toLowerCase() === 'f'
      ) {
        event.preventDefault();
        setSearchOpen(true);
        setSearchFocus((value) => value + 1);
        return;
      }
      if (!kind || !(event.ctrlKey || event.metaKey) || event.altKey) return;
      if (event.key === '+' || event.key === '=') zoomByRef.current(1);
      else if (event.key === '-' || event.key === '_') zoomByRef.current(-1);
      else if (event.key === '0') zoomResetRef.current();
      else return;
      event.preventDefault();
    };
    let lastWheel = 0;
    const onWheel = (event: WheelEvent): void => {
      if (!kind || !(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      // Bàn di chuột bắn hàng chục sự kiện mỗi cú vuốt: một bước mỗi 120 ms.
      if (event.timeStamp - lastWheel < 120) return;
      lastWheel = event.timeStamp;
      zoomByRef.current(event.deltaY < 0 ? 1 : -1);
    };
    window.addEventListener('keydown', onKey);
    root.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('keydown', onKey);
      root.removeEventListener('wheel', onWheel);
    };
  }, [kind]);

  const context = useMemo(() => ({ scale, register: setCaps }), [scale]);

  return (
    <ControlsContext.Provider value={context}>
      <div ref={rootRef} className="flex h-full flex-col">
        {kind || caps.pages || outline || search ? (
          <PreviewToolbar
            embedded={toolbarSlot !== null}
            slot={toolbarSlot}
            kind={kind}
            scale={scale}
            effectiveScale={effectiveScale}
            reflow={reflow}
            pages={caps.pages}
            outline={outline ? { open: outlineOpen, toggle: toggleOutline } : undefined}
            search={
              search
                ? {
                    api: search,
                    open: searchOpen,
                    focusToken: searchFocus,
                    setOpen: setSearchOpen,
                  }
                : undefined
            }
            onZoom={zoomBy}
            onReset={zoomReset}
            onScale={setScale}
          />
        ) : null}
        <div className="relative flex min-h-0 flex-1">
          {outline && outlineOpen ? (
            <OutlineAside outline={outline} onClose={toggleOutline} />
          ) : null}
          <div ref={areaRef} className="relative min-h-0 min-w-0 flex-1 overflow-auto">
            {kind === 'reflow' ? (
              // `zoom` trên khung **trong** vùng cuộn: đặt lên chính vùng cuộn thì
              // thanh cuộn cũng phóng theo.
              <div className="min-h-full" style={{ zoom: reflow / 100 }} data-zoom={reflow}>
                {children}
              </div>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </ControlsContext.Provider>
  );
}

function isEditable(target: Node | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

function PreviewToolbar({
  embedded,
  slot,
  kind,
  pages,
  outline,
  search,
  ...zoom
}: {
  /** Nằm trong header của hộp: không viền dưới, không sticky, không nền riêng. */
  readonly embedded: boolean;
  readonly slot: HTMLElement | null;
  readonly kind: ZoomKind | undefined;
  readonly scale: ScaleZoom;
  readonly effectiveScale: number;
  readonly reflow: number;
  readonly pages: ViewerPages | undefined;
  readonly outline: { readonly open: boolean; readonly toggle: () => void } | undefined;
  readonly search: SearchState | undefined;
  readonly onZoom: (direction: 1 | -1) => void;
  readonly onReset: () => void;
  readonly onScale: (value: ScaleZoom) => void;
}): ReactNode {
  const { t } = useTranslation();
  const toolbar = (
    <div
      role="toolbar"
      aria-label={t('myFiles.preview.toolbar.label')}
      className={cn(
        'flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1',
        embedded
          ? 'justify-center'
          : 'sticky top-0 z-10 border-b border-border bg-background px-3 py-1',
      )}
    >
      {outline ? (
        <Button
          size="icon"
          variant={outline.open ? 'secondary' : 'ghost'}
          className="size-8"
          aria-label={t('myFiles.preview.outline.toggle')}
          title={t('myFiles.preview.outline.toggle')}
          aria-pressed={outline.open}
          onClick={outline.toggle}
        >
          <ListTree aria-hidden />
        </Button>
      ) : null}
      {kind ? <ZoomControls kind={kind} {...zoom} /> : null}
      {pages ? <PageControls pages={pages} /> : null}
      {search ? <SearchControls {...search} /> : null}
    </div>
  );
  return slot ? createPortal(toolbar, slot) : toolbar;
}

/**
 * `[▲] [ô số] / N [▼]` — APM-PVW-b.
 *
 * Ô số giữ **bản nháp** riêng khi đang gõ: đồng bộ thẳng với `current` thì cuộn
 * trong lúc gõ (trang tự đổi) xoá mất chữ người ta đang nhập.
 */
function PageControls({ pages }: { readonly pages: ViewerPages }): ReactNode {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  const go = (page: number): void => pages.goTo(clampPage(page, pages.total));

  return (
    <div className="flex items-center gap-1 text-xs">
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        aria-label={t('myFiles.preview.toolbar.prevPage')}
        title={t('myFiles.preview.toolbar.prevPage')}
        disabled={pages.current <= 1}
        onClick={() => go(pages.current - 1)}
      >
        <ChevronUp aria-hidden />
      </Button>
      <input
        type="text"
        inputMode="numeric"
        aria-label={t('myFiles.preview.toolbar.goToPage')}
        className="h-7 w-12 rounded-DEFAULT border border-input bg-background px-1 text-center tabular-nums"
        value={draft ?? String(pages.current)}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => setDraft(null)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && draft !== null) {
            go(Number(draft.trim()));
            setDraft(null);
          } else if (event.key === 'Escape' && draft !== null) {
            // Esc ở ô số chỉ bỏ bản nháp, không đóng cả hộp xem.
            event.stopPropagation();
            setDraft(null);
          }
        }}
      />
      <span className="tabular-nums text-muted-foreground">
        {t('myFiles.preview.toolbar.pageOf', { total: pages.total })}
      </span>
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        aria-label={t('myFiles.preview.toolbar.nextPage')}
        title={t('myFiles.preview.toolbar.nextPage')}
        disabled={pages.current >= pages.total}
        onClick={() => go(pages.current + 1)}
      >
        <ChevronDown aria-hidden />
      </Button>
    </div>
  );
}

function ZoomControls({
  kind,
  scale,
  effectiveScale,
  reflow,
  onZoom,
  onReset,
  onScale,
}: {
  readonly kind: ZoomKind;
  readonly scale: ScaleZoom;
  readonly effectiveScale: number;
  readonly reflow: number;
  readonly onZoom: (direction: 1 | -1) => void;
  readonly onReset: () => void;
  readonly onScale: (value: ScaleZoom) => void;
}): ReactNode {
  const { t } = useTranslation();
  const levels = kind === 'scale' ? SCALE_LEVELS : REFLOW_LEVELS;
  const current = kind === 'scale' ? effectiveScale : reflow;
  const atMin = current <= levels[0]! + 0.5;
  const atMax = current >= levels.at(-1)! - 0.5;
  const percent = `${Math.round(current)}%`;

  return (
    <div className="flex items-center gap-1">
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        aria-label={t(
          kind === 'scale'
            ? 'myFiles.preview.toolbar.zoomOut'
            : 'myFiles.preview.toolbar.textSmaller',
        )}
        title={t(
          kind === 'scale'
            ? 'myFiles.preview.toolbar.zoomOut'
            : 'myFiles.preview.toolbar.textSmaller',
        )}
        disabled={atMin}
        onClick={() => onZoom(-1)}
      >
        {kind === 'scale' ? (
          <Minus aria-hidden />
        ) : (
          <span aria-hidden className="text-xs">
            A−
          </span>
        )}
      </Button>

      {kind === 'scale' ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="w-24 justify-center tabular-nums"
              aria-label={t('myFiles.preview.toolbar.zoomMenu', { percent })}
            >
              {scale === 'fit' ? t('myFiles.preview.toolbar.fitShort', { percent }) : percent}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
            <DropdownMenuItem onSelect={() => onScale('fit')}>
              {t('myFiles.preview.toolbar.fit')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onScale(100)}>
              {t('myFiles.preview.toolbar.actualSize')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {SCALE_LEVELS.map((level) => (
              <DropdownMenuItem
                key={level}
                className="tabular-nums"
                onSelect={() => onScale(level)}
              >
                {level}%
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span className="w-12 text-center text-xs tabular-nums text-muted-foreground">
          {percent}
        </span>
      )}

      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        aria-label={t(
          kind === 'scale'
            ? 'myFiles.preview.toolbar.zoomIn'
            : 'myFiles.preview.toolbar.textLarger',
        )}
        title={t(
          kind === 'scale'
            ? 'myFiles.preview.toolbar.zoomIn'
            : 'myFiles.preview.toolbar.textLarger',
        )}
        disabled={atMax}
        onClick={() => onZoom(1)}
      >
        {kind === 'scale' ? (
          <Plus aria-hidden />
        ) : (
          <span aria-hidden className="text-sm">
            A+
          </span>
        )}
      </Button>

      {kind === 'scale' ? (
        <Button size="sm" variant="ghost" disabled={scale === 'fit'} onClick={() => onScale('fit')}>
          {t('myFiles.preview.toolbar.fit')}
        </Button>
      ) : (
        <Button size="sm" variant="ghost" disabled={reflow === 100} onClick={onReset}>
          {t('myFiles.preview.toolbar.textReset')}
        </Button>
      )}
    </div>
  );
}

/**
 * Cột dàn ý — APM-PVW-c. Màn ≥ `lg`: một cột cạnh tài liệu, vùng xem co lại (các
 * trình xem `scale` đo lại bề ngang nên "vừa khung" tính theo chỗ còn lại). Màn
 * hẹp: ngăn kéo phủ lên tài liệu, chọn một mục thì tự đóng để thấy chỗ vừa tới.
 */
function OutlineAside({
  outline,
  onClose,
}: {
  readonly outline: ViewerOutline;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const activeRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const thumbnails = outline.thumbnails;
  const fold = useOutlineFold(outline.entries);
  // Mục đang đọc nằm trong nhánh đang gập ⇒ tô mục cha còn hiện.
  const activeIndex = outline.entries.findIndex((entry) => entry.id === outline.active);
  const shownActive = activeIndex < 0 ? -1 : visibleOwnerOf(fold.view, activeIndex);
  // Mục đang đọc luôn trong tầm mắt khi cuộn tài liệu dài.
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [shownActive]);
  const [width, setWidth] = useState(readStoredOutlineWidth);
  const resize = (next: number): void => {
    const clamped = clampOutlineWidth(next);
    setWidth(clamped);
    storeOutlineWidth(clamped);
  };

  return (
    // Khung ngoài mang vị trí + độ rộng; danh sách cuộn bên trong, để tay kéo ở
    // mép phải không nằm trong vùng cuộn (và không trôi đi khi cuộn danh sách).
    <div
      className="absolute inset-y-0 left-0 z-20 flex w-72 max-w-[85%] shrink-0 border-r border-border bg-background shadow-lg lg:relative lg:w-[var(--outline-width)] lg:max-w-[50%] lg:shadow-none"
      style={{ '--outline-width': `${width}px` } as CSSProperties}
    >
      <nav
        ref={navRef}
        aria-label={t('myFiles.preview.outline.title')}
        className="min-w-0 flex-1 overflow-y-auto py-2"
      >
        <div className="flex items-center justify-between gap-2 px-3 pb-1">
          <p className="text-xs font-medium text-muted-foreground">
            {t('myFiles.preview.outline.title')}
          </p>
          {thumbnails ? null : <OutlineLevelBar fold={fold} />}
        </div>
        {thumbnails ? (
          <ol className="flex flex-col gap-3 px-3 pb-2 pt-1">
            {outline.entries.map((entry, index) => {
              const isActive = entry.id === outline.active;
              return (
                <li key={entry.id}>
                  <button
                    ref={isActive ? activeRef : undefined}
                    type="button"
                    aria-label={entry.text}
                    aria-current={isActive ? 'location' : undefined}
                    title={entry.text}
                    className={cn(
                      'relative block w-full rounded-md border-2 p-1 text-left transition-colors hover:border-primary/60',
                      isActive ? 'border-primary' : 'border-transparent',
                    )}
                    onClick={() => {
                      outline.goTo(entry.id);
                      if (!window.matchMedia?.('(min-width: 1024px)').matches) onClose();
                    }}
                  >
                    <OutlineThumbnail
                      id={entry.id}
                      thumbnails={thumbnails}
                      width={width - 32}
                      scrollerRef={navRef}
                    />
                    <span
                      aria-hidden
                      className={cn(
                        'absolute bottom-2 left-2 rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums shadow-sm',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background/90 text-muted-foreground',
                      )}
                    >
                      {index + 1}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        ) : (
        <ul>
          {outline.entries.map((entry, index) => {
            if (!fold.view.visible[index]) return null;
            const isActive = index === shownActive;
            return (
              <li
                key={entry.id}
                className={cn('flex items-center gap-0.5 hover:bg-accent', isActive && 'bg-accent')}
                style={{
                  paddingLeft: (fold.foldable ? 4 : 12) + (Math.min(entry.level, 6) - 1) * 12,
                }}
              >
                <OutlineChevron fold={fold} index={index} title={entry.text} />
                <button
                  ref={isActive ? activeRef : undefined}
                  type="button"
                  aria-current={isActive ? 'location' : undefined}
                  className={cn(
                    'block min-w-0 flex-1 truncate py-1 pr-3 text-left text-sm',
                    isActive ? 'font-medium text-foreground' : 'text-muted-foreground',
                  )}
                  title={entry.text}
                  onClick={() => {
                    outline.goTo(entry.id);
                    if (!window.matchMedia?.('(min-width: 1024px)').matches) onClose();
                  }}
                >
                  {entry.text}
                </button>
              </li>
            );
          })}
        </ul>
        )}
      </nav>
      <OutlineResizeHandle width={width} onResize={resize} />
    </div>
  );
}

/**
 * Một bản chụp nhỏ trong cột dàn ý. Chỉ vẽ khi khung đã vào (hoặc sắp vào) tầm
 * nhìn và **gỡ** khi ra xa: mỗi bản chụp là một cây DOM slide thật được thu nhỏ,
 * không phải ảnh bitmap, nên một bộ 200 slide vẽ hết một lượt là 200 cây DOM.
 * Đổi bề ngang (kéo cột) thì vẽ lại, có hoãn một nhịp để lúc đang kéo không vẽ
 * lại theo từng pixel. Khung giữ tỉ lệ ngay từ đầu nên danh sách không nhảy.
 *
 * `inert`: nội dung slide có thể chứa link; nằm trong `<button>` thì chúng
 * không được nhận phím hay cú bấm riêng — cả ô là một nút "tới slide này".
 *
 * `render` đi qua ref, **không** nằm trong phụ thuộc: mỗi lần mục đang đọc đổi
 * (bấm một khung, cuộn tài liệu) trình xem khai lại và object `thumbnails` là
 * object mới — đưa nó vào phụ thuộc là gỡ rồi vẽ lại mọi khung, cột nháy một
 * nhịp sau mỗi cú bấm (người dùng báo 2026-10-05). Chỉ tỉ lệ và bề ngang mới
 * là lý do vẽ lại.
 */
function OutlineThumbnail({
  id,
  thumbnails,
  width,
  scrollerRef,
}: {
  readonly id: string;
  readonly thumbnails: ViewerOutlineThumbnails;
  readonly width: number;
  readonly scrollerRef: RefObject<HTMLElement | null>;
}): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const renderRef = useRef(thumbnails.render);
  renderRef.current = thumbnails.render;
  const { aspect } = thumbnails;
  // Không có IntersectionObserver (jsdom, trình duyệt cũ) thì vẽ tất cả — vẫn đúng, chỉ nặng hơn.
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setNear(entry.isIntersecting);
      },
      { root: scrollerRef.current, rootMargin: '150% 0px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [scrollerRef]);
  useEffect(() => {
    const element = ref.current;
    if (!element || !near) return;
    let dispose: (() => void) | null = null;
    const timer = window.setTimeout(() => {
      // Bề ngang thật của khung thắng con số ước lượng; jsdom đo ra 0 thì dùng ước lượng.
      dispose = renderRef.current(id, element, element.clientWidth || Math.max(width, 1));
    }, 60);
    return () => {
      window.clearTimeout(timer);
      dispose?.();
    };
  }, [near, id, aspect, width]);
  return (
    <div
      ref={ref}
      inert
      data-testid="outline-thumbnail"
      className="w-full overflow-hidden rounded bg-white shadow-sm ring-1 ring-border"
      style={{ aspectRatio: String(aspect) }}
    />
  );
}

/**
 * Tay kéo độ rộng cột dàn ý — chỉ ở màn ≥ `lg` (màn hẹp cột là ngăn kéo phủ).
 * Kéo chuột, mũi tên trái/phải (16 px một bước), bấm đúp về mặc định.
 * Dùng chung với dàn ý của chế độ đọc `.chilldoc` (`ReadingOutline`).
 */
export function OutlineResizeHandle({
  width,
  onResize,
}: {
  readonly width: number;
  readonly onResize: (width: number) => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('myFiles.preview.outline.resize')}
      title={t('myFiles.preview.outline.resize')}
      aria-valuenow={width}
      aria-valuemin={OUTLINE_MIN_WIDTH}
      aria-valuemax={OUTLINE_MAX_WIDTH}
      tabIndex={0}
      className="absolute inset-y-0 -right-1 z-10 hidden w-2 cursor-col-resize touch-none select-none outline-none hover:bg-primary/30 focus-visible:bg-primary/40 lg:block"
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const startX = event.clientX;
        const start = width;
        const handle = event.currentTarget;
        handle.setPointerCapture?.(event.pointerId);
        const move = (next: PointerEvent): void => onResize(start + next.clientX - startX);
        const up = (): void => {
          handle.removeEventListener('pointermove', move);
          handle.removeEventListener('pointerup', up);
          handle.removeEventListener('pointercancel', up);
        };
        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', up);
        handle.addEventListener('pointercancel', up);
      }}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        onResize(width + (event.key === 'ArrowRight' ? 16 : -16));
      }}
      onDoubleClick={() => onResize(OUTLINE_DEFAULT_WIDTH)}
    />
  );
}

export interface DomHeading {
  readonly element: HTMLElement;
  readonly level: number;
  readonly text: string;
}

/**
 * Dàn ý cho trình xem dựng trên DOM (markdown, chilldoc, docx): mục = phần tử
 * tiêu đề, `goTo` = cuộn tới nó, mục đang đọc = tiêu đề cuối cùng đã qua mép
 * trên vùng cuộn. Tính một lần mỗi khung hình (rAF).
 *
 * `headings` phải là **cùng mảng** giữa các lần render (state hoặc memo).
 */
export function useDomOutline(headings: readonly DomHeading[]): ViewerOutline | undefined {
  const entries = useMemo(
    () =>
      headings.map((heading, index) => ({
        id: String(index),
        level: heading.level,
        text: heading.text,
      })),
    [headings],
  );
  const [active, setActive] = useState<string | undefined>(undefined);
  // Mục vừa bấm: giữ sáng chừng nào tiêu đề ấy còn trong khung — gần cuối bài
  // nhiều tiêu đề cùng hiện, và người ta muốn thấy đúng mục mình vừa chọn.
  const pinned = useRef<number | null>(null);

  useEffect(() => {
    pinned.current = null;
    const first = headings[0];
    if (!first) return;
    const scroller = scrollParentOf(first.element);
    let frame = 0;
    const update = (): void => {
      frame = 0;
      const box = scroller?.getBoundingClientRect();
      const top = (box?.top ?? 0) + 32;
      const bottom = box?.bottom ?? window.innerHeight;
      // Cuộn chạm đáy: các tiêu đề cuối không bao giờ lên tới mép trên được, nên
      // "đang đọc" là tiêu đề cuối còn thấy — bấm mục cuối thì mục cuối sáng.
      const atEnd = scroller
        ? scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2
        : window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
      const pin = pinned.current === null ? undefined : headings[pinned.current];
      if (pin) {
        const at = pin.element.getBoundingClientRect().top;
        if (at >= top - 64 && at < bottom) {
          setActive(String(pinned.current));
          return;
        }
        pinned.current = null;
      }
      let index = 0;
      headings.forEach((heading, i) => {
        const at = heading.element.getBoundingClientRect().top;
        if (at <= top || (atEnd && at < bottom)) index = i;
      });
      setActive(String(index));
    };
    const onScroll = (): void => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    const target: HTMLElement | Window = scroller ?? window;
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [headings]);

  const goTo = useCallback(
    (id: string) => {
      const heading = headings[Number(id)];
      if (!heading) return;
      pinned.current = Number(id);
      setActive(id);
      heading.element.scrollIntoView({ block: 'start' });
    },
    [headings],
  );
  return entries.length > 0 ? { entries, active, goTo } : undefined;
}

/** Khung cuộn gần nhất; không có (trang chia sẻ cuộn cả trang) ⇒ `null`. */
function scrollParentOf(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return node;
  }
  return null;
}

interface SearchState {
  readonly api: ViewerSearch;
  readonly open: boolean;
  readonly focusToken: number;
  readonly setOpen: (open: boolean) => void;
}

/**
 * Ô tìm — APM-PVW-d. Gõ ⇒ tìm sau 200 ms; Enter kế, Shift+Enter trước (quay
 * vòng); Esc xoá tô và đóng (không đóng cả hộp xem).
 */
function SearchControls({ api, open, focusToken, setOpen }: SearchState): ReactNode {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<{ total: number; current: number } | null>(null);
  const [pending, setPending] = useState(false);
  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    if (!open || focusToken === 0) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [open, focusToken]);

  useEffect(() => {
    if (!open || !query.trim()) {
      apiRef.current.clear();
      setResult(null);
      setPending(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setPending(true);
      void apiRef.current.run(query, controller.signal).then(
        (total) => {
          if (controller.signal.aborted) return;
          setPending(false);
          setResult({ total, current: total > 0 ? 0 : -1 });
          if (total > 0) apiRef.current.go(0);
        },
        () => {
          if (!controller.signal.aborted) setPending(false);
        },
      );
    }, 200);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [open, query]);

  useEffect(() => () => apiRef.current.clear(), []);

  const step = (direction: 1 | -1): void => {
    if (!result || result.total === 0) return;
    const next = (result.current + direction + result.total) % result.total;
    setResult({ ...result, current: next });
    api.go(next);
  };
  const close = (): void => {
    setQuery('');
    setOpen(false);
  };

  if (!open) {
    return (
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        aria-label={t('myFiles.preview.search.open')}
        title={t('myFiles.preview.search.open')}
        onClick={() => setOpen(true)}
      >
        <Search aria-hidden />
      </Button>
    );
  }

  const status = pending
    ? t('myFiles.preview.search.searching')
    : result === null
      ? ''
      : result.total === 0
        ? t('myFiles.preview.search.none')
        : t('myFiles.preview.search.count', {
            current: result.current + 1,
            total:
              result.total >= MAX_SEARCH_RESULTS
                ? `${MAX_SEARCH_RESULTS.toLocaleString(i18n.language)}+`
                : result.total,
          });

  return (
    <div role="search" className="flex items-center gap-1 text-xs">
      <Search aria-hidden className="size-4 text-muted-foreground" />
      <input
        ref={inputRef}
        type="search"
        autoFocus
        aria-label={t('myFiles.preview.search.placeholder')}
        placeholder={t('myFiles.preview.search.placeholder')}
        className="h-7 w-44 rounded-DEFAULT border border-input bg-background px-2 text-sm"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            step(event.shiftKey ? -1 : 1);
          } else if (event.key === 'Escape') {
            // Esc ở ô tìm chỉ đóng ô tìm, không đóng cả hộp xem.
            event.stopPropagation();
            event.preventDefault();
            close();
          }
        }}
      />
      <span aria-live="polite" className="min-w-16 tabular-nums text-muted-foreground">
        {status}
      </span>
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        aria-label={t('myFiles.preview.search.prev')}
        title={t('myFiles.preview.search.prev')}
        disabled={!result || result.total === 0}
        onClick={() => step(-1)}
      >
        <ChevronUp aria-hidden />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        aria-label={t('myFiles.preview.search.next')}
        title={t('myFiles.preview.search.next')}
        disabled={!result || result.total === 0}
        onClick={() => step(1)}
      >
        <ChevronDown aria-hidden />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        aria-label={t('myFiles.preview.search.close')}
        title={t('myFiles.preview.search.close')}
        onClick={close}
      >
        <X aria-hidden />
      </Button>
    </div>
  );
}

const HIGHLIGHT_ALL = 'apm-preview-search';
const HIGHLIGHT_CURRENT = 'apm-preview-search-current';

interface HighlightRegistryLike {
  set(name: string, highlight: unknown): void;
  delete(name: string): void;
}

/** CSS Custom Highlight API — thiếu (trình duyệt cũ) thì vẫn tìm + cuộn tới, chỉ không tô. */
function highlightRegistry(): {
  registry: HighlightRegistryLike;
  make: (...ranges: Range[]) => unknown;
} | null {
  const registry = (globalThis.CSS as { highlights?: HighlightRegistryLike } | undefined)
    ?.highlights;
  const HighlightCtor = (globalThis as { Highlight?: new (...ranges: Range[]) => unknown })
    .Highlight;
  if (!registry || !HighlightCtor) return null;
  return { registry, make: (...ranges) => new HighlightCtor(...ranges) };
}

/**
 * Tìm trên DOM của vùng xem: dựng `Range` cho mọi kết quả và tô bằng CSS Custom
 * Highlight — **không sửa DOM**: DOM của chilldoc là của TipTap, của docx là của
 * docx-preview, chèn `<mark>` vào là làm hỏng chúng.
 */
function createDomSearch(getRoot: () => HTMLElement | null): ViewerSearch {
  let ranges: Range[] = [];
  const paint = (current: number): void => {
    const api = highlightRegistry();
    if (!api) return;
    api.registry.set(HIGHLIGHT_ALL, api.make(...ranges));
    const active = ranges[current];
    if (active) api.registry.set(HIGHLIGHT_CURRENT, api.make(active));
    else api.registry.delete(HIGHLIGHT_CURRENT);
  };
  return {
    run: (query) => {
      const root = getRoot();
      if (!root) return Promise.resolve(0);
      const index = indexText(root);
      ranges = rangesOf(index, findAll(index.text, query));
      paint(-1);
      return Promise.resolve(ranges.length);
    },
    go: (index) => {
      const range = ranges[index];
      if (!range) return;
      paint(index);
      scrollRangeIntoView(range);
    },
    clear: () => {
      ranges = [];
      const api = highlightRegistry();
      api?.registry.delete(HIGHLIGHT_ALL);
      api?.registry.delete(HIGHLIGHT_CURRENT);
    },
  };
}

/** Cuộn khung chứa gần nhất cho kết quả nằm ở một phần ba trên của khung (không `scrollIntoView` trên text node). */
function scrollRangeIntoView(range: Range): void {
  const host = range.startContainer.parentElement;
  // jsdom (và vài môi trường cũ) không có `Range.getBoundingClientRect`: vẫn tô, chỉ không cuộn.
  if (!host || typeof range.getBoundingClientRect !== 'function') return;
  const rect = range.getBoundingClientRect();
  const scroller = scrollParentOf(host);
  if (!scroller) {
    window.scrollBy({ top: rect.top - window.innerHeight / 3 });
    return;
  }
  const box = scroller.getBoundingClientRect();
  if (rect.top < box.top + 24 || rect.bottom > box.bottom - 24) {
    scroller.scrollTop += rect.top - box.top - box.height / 3;
  }
  if (rect.left < box.left || rect.right > box.right) {
    scroller.scrollLeft += rect.left - box.left - box.width / 3;
  }
}
